import { useEffect, useMemo, useState } from 'react';
import { Button, EmptyState, Label, Select } from '../components/ui';
import {
    groupRouteBySection,
    useServiceRoute,
    useUnassignedForDate,
} from '../lib/serviceQueries';
import type { ServiceItemWithContext } from '../lib/serviceTypes';
import { communityLabel } from '../lib/serviceTypes';
import { cn } from '../lib/utils';
import { useCrewEmail, useCrewEmailSends } from '../lib/crewEmail';
import { SendRouteDialog } from './SendRouteDialog';

/**
 * The daily route.
 *
 * Sara builds this by hand every afternoon: filter the master list to one tech,
 * then group the stops so nearby communities land on the same day rather than
 * sending someone back and forth across the metro. The community sections sheet
 * is what defines "nearby", and this reads it straight from the data.
 *
 * Two deliberate choices:
 *
 *  - Sections render in the order the sheet defines, and a stop whose community
 *    has no section sinks to the bottom rather than leading the day.
 *
 *  - Work scheduled for the date with NO crew assigned is shown separately and
 *    in amber. The route filters by crew, so those stops appear on nobody's day.
 *    Hiding them means the first person to notice is the builder.
 */
export function ServiceRouteView({
    crews,
}: {
    crews: { id: string; name: string }[];
}) {
    const [crewId, setCrewId] = useState<string>('');
    const [sendOpen, setSendOpen] = useState(false);
    const [isoDate, setIsoDate] = useState<string>(() =>
        new Date().toISOString().slice(0, 10),
    );

    // Default to the first crew once they load, so the view is never a blank
    // screen waiting on two dropdowns.
    useEffect(() => {
        if (!crewId && crews.length) setCrewId(crews[0].id);
    }, [crews, crewId]);

    const { data: stops = [], isLoading } = useServiceRoute(crewId || undefined, isoDate);
    const { data: unassigned = [] } = useUnassignedForDate(isoDate);

    const groups = useMemo(() => groupRouteBySection(stops), [stops]);
    const crewName = crews.find((c) => c.id === crewId)?.name ?? '';

    // Phase 9. The address and the send history sit next to the Export button
    // because this is already the screen where a day gets picked and sent.
    const { data: crewEmail } = useCrewEmail(crewId || undefined);
    const { data: sends = [] } = useCrewEmailSends(crewId || undefined, isoDate);
    const lastSent = sends.find((x) => x.status === 'sent') ?? null;

    function shiftDay(days: number) {
        const d = new Date(`${isoDate}T12:00:00`);
        d.setDate(d.getDate() + days);
        setIsoDate(d.toISOString().slice(0, 10));
    }

    function exportRoute() {
        const cell = (v: string | null | undefined) =>
            `"${String(v ?? '').replace(/\r?\n/g, ' ').replace(/"/g, '""')}"`;
        const lines: string[] = [
            [`${crewName} route`, isoDate].map(cell).join(','),
            '',
            ['Section', 'Community', 'Lot', 'Address', 'Type of Work', 'Concrete', 'Extra time', 'Notes']
                .map(cell)
                .join(','),
        ];
        for (const g of groups) {
            for (const s of g.items) {
                lines.push(
                    [
                        g.label,
                        communityLabel(s),
                        s.lot,
                        s.address,
                        s.type_of_work,
                        s.requires_concrete ? 'Yes' : '',
                        s.extended_time ? 'Yes' : '',
                        s.notes,
                    ]
                        .map(cell)
                        .join(','),
                );
            }
        }
        const blob = new Blob(['\uFEFF' + lines.join('\r\n')], {
            type: 'text/csv;charset=utf-8;',
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `route-${crewName.replace(/\s+/g, '-')}-${isoDate}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }

    return (
        <div className="flex-1 min-h-0 overflow-auto">
            <div className="px-4 md:px-6 py-5 max-w-4xl">
                <div className="flex flex-wrap items-end gap-4 mb-5">
                    <div className="min-w-[170px]">
                        <Label htmlFor="route-crew">Crew or tech</Label>
                        <Select
                            id="route-crew"
                            value={crewId}
                            onChange={(e) => setCrewId(e.target.value)}
                        >
                            {crews.map((c) => (
                                <option key={c.id} value={c.id}>
                                    {c.name}
                                </option>
                            ))}
                        </Select>
                    </div>

                    <div>
                        <Label htmlFor="route-date">Date</Label>
                        <div className="flex items-center gap-1">
                            <Button variant="ghost" size="sm" onClick={() => shiftDay(-1)}>
                                &lsaquo;
                            </Button>
                            <input
                                id="route-date"
                                type="date"
                                value={isoDate}
                                onChange={(e) => setIsoDate(e.target.value)}
                                className="bg-white dark:bg-graphite-900 border border-graphite-300 dark:border-graphite-700 rounded px-2 py-2 font-mono text-sm text-graphite-900 dark:text-graphite-100 focus:outline-none focus:ring-1 focus:ring-signal-500"
                            />
                            <Button variant="ghost" size="sm" onClick={() => shiftDay(1)}>
                                &rsaquo;
                            </Button>
                        </div>
                    </div>

                    <div className="ml-auto flex items-center gap-3 pb-1">
                        <span className="font-mono text-xs text-graphite-500 tabular">
                            {stops.length} {stops.length === 1 ? 'stop' : 'stops'}
                        </span>
                        <Button
                            variant="primary"
                            size="sm"
                            onClick={() => setSendOpen(true)}
                            disabled={!crewId || stops.length === 0}
                            title={
                                crewEmail
                                    ? `Send this list to ${crewEmail}`
                                    : 'No email address on file for this crew yet'
                            }
                        >
                            Email list
                        </Button>
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={exportRoute}
                            disabled={stops.length === 0}
                        >
                            Export
                        </Button>
                    </div>
                </div>

                {/* Answers "did this already go out" without opening a dialog,
                    which is the question the send log exists for. */}
                {crewId && lastSent ? (
                    <div className="mb-4 font-mono text-[11px] text-graphite-500">
                        Last sent to {lastSent.recipient} with {lastSent.item_count}{' '}
                        {lastSent.item_count === 1 ? 'stop' : 'stops'}
                        {sends.length > 1 ? ` · ${sends.length} sends for this date` : ''}
                    </div>
                ) : null}

                {unassigned.length > 0 && (
                    <div className="mb-5 border border-amber-500/40 bg-amber-500/10 rounded px-4 py-3">
                        <div className="font-mono text-[10px] uppercase tracking-widest text-amber-600 dark:text-amber-400 mb-2">
                            {unassigned.length} scheduled for this date with no crew
                        </div>
                        <div className="space-y-1">
                            {unassigned.map((s) => (
                                <div
                                    key={s.id}
                                    className="text-sm text-graphite-800 dark:text-graphite-200"
                                >
                                    <span className="font-medium">{communityLabel(s)}</span>
                                    {s.lot ? (
                                        <span className="font-mono tabular text-graphite-600 dark:text-graphite-400">
                                            {' '}
                                            Lot {s.lot}
                                        </span>
                                    ) : null}
                                    <span className="text-graphite-600 dark:text-graphite-400">
                                        {' \u2014 '}
                                        {s.type_of_work}
                                    </span>
                                </div>
                            ))}
                        </div>
                        <p className="mt-2 text-[11px] text-amber-600 dark:text-amber-400">
                            These appear on nobody&apos;s route until a crew is assigned on the list.
                        </p>
                    </div>
                )}

                {isLoading ? (
                    <div className="font-mono text-xs text-graphite-500 uppercase tracking-widest">
                        Loading route...
                    </div>
                ) : stops.length === 0 ? (
                    <EmptyState
                        title="Nothing scheduled"
                        description={`${crewName || 'This crew'} has no open items scheduled for ${isoDate}.`}
                    />
                ) : (
                    <div className="space-y-6">
                        {groups.map((g) => (
                            <div key={g.label}>
                                <div className="flex items-baseline gap-2 mb-2 pb-1 border-b border-graphite-200 dark:border-graphite-800">
                                    <h2
                                        className={cn(
                                            'font-mono text-[11px] uppercase tracking-[0.15em]',
                                            g.sortOrder === 999
                                                ? 'text-amber-600 dark:text-amber-400'
                                                : 'text-graphite-600 dark:text-graphite-400',
                                        )}
                                    >
                                        {g.label}
                                    </h2>
                                    <span className="font-mono text-[10px] text-graphite-500 tabular">
                                        {g.items.length}
                                    </span>
                                </div>
                                <div className="divide-y divide-graphite-200 dark:divide-graphite-900">
                                    {g.items.map((s) => (
                                        <RouteStop key={s.id} stop={s} />
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <SendRouteDialog
                open={sendOpen}
                onClose={() => setSendOpen(false)}
                crew={crewId ? { id: crewId, name: crewName } : null}
                isoDate={isoDate}
            />
        </div>
    );
}

function RouteStop({ stop }: { stop: ServiceItemWithContext }) {
    return (
        <div className="py-3 flex gap-4">
            <div className="w-52 shrink-0">
                <div className="text-sm font-medium text-graphite-900 dark:text-graphite-100">
                    {communityLabel(stop)}
                </div>
                <div className="font-mono text-xs tabular text-graphite-600 dark:text-graphite-400">
                    {stop.lot ? `Lot ${stop.lot}` : '\u2014'}
                </div>
                {stop.address && (
                    <div className="text-[11px] text-graphite-500 mt-0.5">{stop.address}</div>
                )}
            </div>
            <div className="min-w-0 flex-1">
                <div className="text-sm text-graphite-800 dark:text-graphite-200 break-words">
                    {stop.type_of_work}
                </div>
                {stop.notes && (
                    <div className="mt-1 text-[11px] text-graphite-600 dark:text-graphite-400 break-words">
                        {stop.notes}
                    </div>
                )}
                <div className="mt-1 flex flex-wrap gap-1.5">
                    {stop.requires_concrete && (
                        <span className="font-mono text-[9px] uppercase tracking-widest px-1 py-0.5 rounded bg-concrete-500 text-white">
                            Concrete
                        </span>
                    )}
                    {stop.extended_time && (
                        <span className="font-mono text-[9px] uppercase tracking-widest px-1 py-0.5 rounded bg-signal-500 text-graphite-900">
                            Extra time
                        </span>
                    )}
                    {stop.is_warranty && (
                        <span className="font-mono text-[9px] uppercase tracking-widest px-1 py-0.5 rounded bg-amber-500 text-graphite-900">
                            Warranty
                        </span>
                    )}
                    {stop.priority <= 2 && (
                        <span className="font-mono text-[9px] uppercase tracking-widest px-1 py-0.5 rounded bg-amber-500 text-graphite-900">
                            P{stop.priority}
                        </span>
                    )}
                    {stop.billable && (
                        <span className="font-mono text-[9px] uppercase tracking-widest px-1 py-0.5 rounded border border-graphite-300 dark:border-graphite-700 text-graphite-600 dark:text-graphite-400">
                            {stop.epo_amount != null ? `$${stop.epo_amount.toFixed(2)}` : 'Billable'}
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
}
